import Initialiser from 'initialiser';
import api from 'utils/api';
import constants from 'utils/constants';
import { pushToast } from 'ducks/modules/toaster';

// ACTION TYPES
const SEARCH_START = 'api_tokens/SEARCH_START';
const SEARCH_SUCCESS = 'api_tokens/SEARCH_SUCCESS';
const SEARCH_FAILURE = 'api_tokens/SEARCH_FAILURE';
const SEARCH_CLEAR = 'api_tokens/SEARCH_CLEAR';

const USER_TOKENS_FETCH_START = 'api_tokens/USER_TOKENS_FETCH_START';
const USER_TOKENS_FETCH_SUCCESS = 'api_tokens/USER_TOKENS_FETCH_SUCCESS';
const USER_TOKENS_FETCH_FAILURE = 'api_tokens/USER_TOKENS_FETCH_FAILURE';

const TOKEN_CREATE_START = 'api_tokens/TOKEN_CREATE_START';
const TOKEN_CREATE_SUCCESS = 'api_tokens/TOKEN_CREATE_SUCCESS';
const TOKEN_CREATE_FAILURE = 'api_tokens/TOKEN_CREATE_FAILURE';

const TOKEN_UPDATE_ENDPOINTS_START = 'api_tokens/TOKEN_UPDATE_ENDPOINTS_START';
const TOKEN_UPDATE_ENDPOINTS_SUCCESS = 'api_tokens/TOKEN_UPDATE_ENDPOINTS_SUCCESS';
const TOKEN_UPDATE_ENDPOINTS_FAILURE = 'api_tokens/TOKEN_UPDATE_ENDPOINTS_FAILURE';

const ENDPOINTS_FETCH_START = 'api_tokens/ENDPOINTS_FETCH_START';
const ENDPOINTS_FETCH_SUCCESS = 'api_tokens/ENDPOINTS_FETCH_SUCCESS';
const ENDPOINTS_FETCH_FAILURE = 'api_tokens/ENDPOINTS_FETCH_FAILURE';

const TOKEN_REVEAL_CLEAR = 'api_tokens/TOKEN_REVEAL_CLEAR';

// SYNC ACTION CREATORS
export const searchStart = () => ({ type: SEARCH_START });
export const searchSuccess = (users) => ({ type: SEARCH_SUCCESS, users });
export const searchFailure = (error) => ({ type: SEARCH_FAILURE, error });
export const clearSearch = () => ({ type: SEARCH_CLEAR });

export const userTokensFetchStart = (bkUserId) => ({ type: USER_TOKENS_FETCH_START, bkUserId });
export const userTokensFetchSuccess = (bkUserId, tokens) => ({
    type: USER_TOKENS_FETCH_SUCCESS,
    bkUserId,
    tokens,
});
export const userTokensFetchFailure = (bkUserId, error) => ({
    type: USER_TOKENS_FETCH_FAILURE,
    bkUserId,
    error,
});

export const tokenCreateStart = () => ({ type: TOKEN_CREATE_START });
export const tokenCreateSuccess = (bkUserId, token) => ({ type: TOKEN_CREATE_SUCCESS, bkUserId, token });
export const tokenCreateFailure = (error) => ({ type: TOKEN_CREATE_FAILURE, error });

export const tokenUpdateEndpointsStart = () => ({ type: TOKEN_UPDATE_ENDPOINTS_START });
export const tokenUpdateEndpointsSuccess = (bkUserId, accessibleEndpoints) => ({
    type: TOKEN_UPDATE_ENDPOINTS_SUCCESS,
    bkUserId,
    accessibleEndpoints,
});
export const tokenUpdateEndpointsFailure = (error) => ({
    type: TOKEN_UPDATE_ENDPOINTS_FAILURE,
    error,
});

export const endpointsFetchStart = () => ({ type: ENDPOINTS_FETCH_START });
export const endpointsFetchSuccess = (endpoints) => ({ type: ENDPOINTS_FETCH_SUCCESS, endpoints });
export const endpointsFetchFailure = (error) => ({ type: ENDPOINTS_FETCH_FAILURE, error });

export const clearTokenReveal = () => ({ type: TOKEN_REVEAL_CLEAR });

// THUNKS

export function fetchEndpointsCatalogue() {
    return async (dispatch) => {
        dispatch(endpointsFetchStart());
        try {
            const endpoints = await api.fetchApiEndpoints();
            dispatch(endpointsFetchSuccess(endpoints));
        } catch (err) {
            dispatch(endpointsFetchFailure(err));
            dispatch(
                pushToast({
                    title: 'Failed to load endpoint catalogue',
                    type: constants.ToastTypes.ERROR,
                }),
            );
        }
    };
}

export function fetchTokensForUser(bkUserId) {
    return async (dispatch) => {
        dispatch(userTokensFetchStart(bkUserId));
        try {
            const tokens = await api.fetchUserApiTokens(bkUserId);
            dispatch(userTokensFetchSuccess(bkUserId, tokens || []));
        } catch (err) {
            dispatch(userTokensFetchFailure(bkUserId, err));
            dispatch(
                pushToast({
                    title: `Failed to load tokens for bk_user_id ${bkUserId}`,
                    type: constants.ToastTypes.ERROR,
                }),
            );
        }
    };
}

/**
 * Resolve a search term to a list of users via the existing /users search endpoint,
 * push them into the duck as the new result set, and lazily fetch each user's tokens.
 */
export function searchUsers(searchBy, searchTerm) {
    return async (dispatch) => {
        dispatch(searchStart());
        try {
            const matches = await api.fetchUsers(searchBy, searchTerm);
            const users = Array.isArray(matches)
                ? matches
                      .filter((m) => m && m.bk_user_id)
                      .map((m) => ({
                          // accountId (bk_user_accounts.id) is unique per row and is used as the
                          // React key — bk_user_id repeats when one bk_user has several accounts.
                          accountId: m.id,
                          bkUserId: m.bk_user_id,
                          username: m.username || `bk_user_id ${m.bk_user_id}`,
                          email: m.email || '',
                          // Carried so token-create can enable DSP access without a re-fetch.
                          userPreferences: m.user_preferences || null,
                      }))
                : [];
            dispatch(searchSuccess(users));
            if (users.length === 0) {
                dispatch(
                    pushToast({
                        title: `No users found for "${searchTerm}"`,
                        type: constants.ToastTypes.WARNING,
                    }),
                );
                return;
            }
            // Lazy-load token info per user — rows render immediately, token state fills in.
            await Promise.all(users.map((u) => dispatch(fetchTokensForUser(u.bkUserId))));
        } catch (err) {
            dispatch(searchFailure(err));
            dispatch(
                pushToast({
                    title: `Error searching for "${searchTerm}"`,
                    type: constants.ToastTypes.ERROR,
                }),
            );
        }
    };
}

/**
 * Enable DSP dashboard + API access for an account by merging the two flags into its
 * existing user_preferences and PUTting the whole object back (the BE replaces, not
 * merges, so we must send the full set). Best-effort: a token has already been minted,
 * so a preference failure only warns — it never rolls the token back.
 */
export function enableApiAccess(accountId, userPreferences) {
    return async (dispatch) => {
        if (!accountId) {
            return;
        }
        const base = userPreferences && typeof userPreferences === 'object' ? userPreferences : {};
        const nextPreferences = {
            ...base,
            dsp_dashboard: true,
            features: {
                ...(base.features || {}),
                api_access: { enabled: true },
            },
        };
        try {
            await api.updateUser(accountId, { user_preferences: nextPreferences });
        } catch {
            dispatch(
                pushToast({
                    title: 'Token created, but enabling DSP dashboard / API access failed — set it manually.',
                    type: constants.ToastTypes.WARNING,
                }),
            );
        }
    };
}

export function createToken(bkUserId, username, accessibleEndpoints, accountId, userPreferences) {
    return async (dispatch) => {
        dispatch(tokenCreateStart());
        try {
            const token = await api.createUserApiToken(bkUserId, accessibleEndpoints);
            dispatch(tokenCreateSuccess(bkUserId, { ...token }));
            // A token implies DSP API usage, so turn on the dashboard + api_access for this account.
            await dispatch(enableApiAccess(accountId, userPreferences));
            // Refresh the user's row so the new (masked) token shows up underneath.
            await dispatch(fetchTokensForUser(bkUserId));
            dispatch(
                pushToast({
                    title: 'Token created — DSP dashboard and API access enabled',
                    type: constants.ToastTypes.SUCCESS,
                }),
            );
        } catch (err) {
            dispatch(tokenCreateFailure(err));
            dispatch(
                pushToast({
                    title: `Failed to create token for ${username || bkUserId}`,
                    type: constants.ToastTypes.ERROR,
                }),
            );
        }
    };
}

// Resolves to true on success, false on failure, so callers can decide whether to
// close the editor — the error itself is fully handled here (failure action + toast).
export function updateTokenEndpoints(bkUserId, accessibleEndpoints) {
    return async (dispatch) => {
        dispatch(tokenUpdateEndpointsStart());
        try {
            await api.updateUserApiTokenEndpoints(bkUserId, accessibleEndpoints);
            dispatch(tokenUpdateEndpointsSuccess(bkUserId, accessibleEndpoints));
            dispatch(
                pushToast({
                    title: accessibleEndpoints.length === 0 ? 'Token disabled' : 'Endpoints updated',
                    type: constants.ToastTypes.SUCCESS,
                }),
            );
            return true;
        } catch (err) {
            dispatch(tokenUpdateEndpointsFailure(err));
            dispatch(pushToast({ title: 'Failed to update token', type: constants.ToastTypes.ERROR }));
            return false;
        }
    };
}

// SELECTORS

export const selectEndpoints = (state) => state.api_tokens.endpoints;
export const selectCreatedToken = (state) => state.api_tokens.createdToken;
export const selectUserLoading = (state) => state.api_tokens.userLoading;
export const selectIsSearching = (state) => state.api_tokens.isSearching;
export const selectSearchPerformed = (state) => state.api_tokens.searchPerformed;
export const selectError = (state) => state.api_tokens.error;

export const isTokenDisabled = (token) =>
    !token || !token.accessible_endpoints || token.accessible_endpoints.length === 0;

/**
 * Returns the current search result list, each enriched with the user's token (if any
 * has been loaded). One token per user is assumed; if the BE returns multiple, we
 * surface the most recently created (last in the array).
 */
export const selectSearchResults = (state) => {
    const { searchResults, tokensByUser } = state.api_tokens;
    return searchResults.map((user) => {
        const slice = tokensByUser[user.bkUserId];
        const tokens = slice ? slice.tokens : [];
        const latestToken = tokens.length > 0 ? tokens[tokens.length - 1] : null;
        return { ...user, token: latestToken };
    });
};

// REDUCER
export default function reducer(state = Initialiser.initialState.api_tokens, action) {
    switch (action.type) {
        case SEARCH_START:
            return { ...state, isSearching: true, error: null };
        case SEARCH_SUCCESS:
            return {
                ...state,
                isSearching: false,
                searchPerformed: true,
                searchResults: action.users,
                // Reset per-user data when a new search begins so stale rows disappear.
                tokensByUser: {},
                userLoading: {},
            };
        case SEARCH_FAILURE:
            return { ...state, isSearching: false, error: action.error };
        case SEARCH_CLEAR:
            return {
                ...state,
                searchResults: [],
                searchPerformed: false,
                tokensByUser: {},
                userLoading: {},
            };

        case USER_TOKENS_FETCH_START:
            return {
                ...state,
                userLoading: { ...state.userLoading, [action.bkUserId]: true },
            };
        case USER_TOKENS_FETCH_SUCCESS:
            return {
                ...state,
                tokensByUser: {
                    ...state.tokensByUser,
                    [action.bkUserId]: { tokens: action.tokens },
                },
                userLoading: { ...state.userLoading, [action.bkUserId]: false },
            };
        case USER_TOKENS_FETCH_FAILURE:
            return {
                ...state,
                userLoading: { ...state.userLoading, [action.bkUserId]: false },
                error: action.error,
            };

        case TOKEN_CREATE_START:
            return { ...state, error: null };
        case TOKEN_CREATE_SUCCESS:
            return { ...state, createdToken: { ...action.token, bkUserId: action.bkUserId } };
        case TOKEN_CREATE_FAILURE:
            return { ...state, error: action.error };

        case TOKEN_UPDATE_ENDPOINTS_SUCCESS: {
            const userSlice = state.tokensByUser[action.bkUserId];
            if (!userSlice) {
                return state;
            }
            return {
                ...state,
                tokensByUser: {
                    ...state.tokensByUser,
                    [action.bkUserId]: {
                        ...userSlice,
                        tokens: userSlice.tokens.map((t) => ({
                            ...t,
                            accessible_endpoints: action.accessibleEndpoints,
                        })),
                    },
                },
            };
        }
        case TOKEN_UPDATE_ENDPOINTS_FAILURE:
            return { ...state, error: action.error };

        case ENDPOINTS_FETCH_SUCCESS:
            return { ...state, endpoints: action.endpoints };

        case TOKEN_REVEAL_CLEAR:
            return { ...state, createdToken: null };

        default:
            return state;
    }
}
